// =====================================================================
// src/components/intl-acca/GroupStandingsCard.tsx
// =====================================================================
// One group's standings table — Hub uses 6 of these in a grid.

import type {
  GroupStanding,
  Nation,
} from "../../db/intl-acca-types-extended";
import { NationFlag } from "./NationFlag";
import { HonestEmptyState } from "./HonestEmptyState";

interface GroupStandingsCardProps {
  group: GroupStanding;
  nations: Record<string, Nation>;
  /** How many top rows qualify (highlighted). Defaults to 2 (group-stage AFCON). */
  qualifyingDepth?: number;
  /** Click a row to go to that team's DNA profile. */
  onTeamClick?: (teamCode: string) => void;
}

export function GroupStandingsCard({
  group,
  nations,
  qualifyingDepth = 2,
  onTeamClick,
}: GroupStandingsCardProps): JSX.Element {
  if (group.rows.length === 0) {
    return (
      <div className="ia-card" style={{ padding: 0 }}>
        <div className="ia-standings-head">
          <span className="ia-eyebrow">GROUP {group.group_id}</span>
        </div>
        <HonestEmptyState
          variant="custom"
          title="No standings yet."
          body="Group standings populate after the first matchday plays."
        />
      </div>
    );
  }

  return (
    <div className="ia-standings">
      <div className="ia-standings-head">
        <span className="ia-eyebrow">GROUP {group.group_id}</span>
        <span
          className="ia-mono"
          style={{
            fontSize: "var(--sz-mono-xs)",
            color: "var(--text-tertiary)",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          MD {group.rows[0]?.p ?? 0}
        </span>
      </div>
      {group.rows.map((row, i) => {
        const nation = nations[row.team] ?? null;
        const qualifying = i < qualifyingDepth;
        return (
          <div
            key={row.team}
            className={`ia-standings-row${qualifying ? " qualifying" : ""}`}
            onClick={() => onTeamClick?.(row.team)}
          >
            <span className={`ia-standings-rank${qualifying ? " qualifying" : ""}`}>
              {i + 1}
            </span>
            <NationFlag nation={nation} size={20} />
            <div>
              <div className="ia-standings-team">{nation?.name ?? row.team}</div>
              {nation?.is_host ? (
                <div
                  className="ia-mono"
                  style={{
                    fontSize: 9,
                    color: "var(--accent)",
                    letterSpacing: "0.18em",
                  }}
                >
                  ★ HOST
                </div>
              ) : null}
            </div>
            <span className="ia-standings-num">{row.p}</span>
            <span className="ia-standings-num">
              {row.gf}-{row.ga}
            </span>
            <span className="ia-standings-pts">{row.pts}</span>
          </div>
        );
      })}
    </div>
  );
}
